import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquareText, Play, Square, Plus } from "lucide-react";
import { useConversation } from "@elevenlabs/react";
import Siriwave from "react-siriwave";
import { cn } from "@/lib/utils";
import { ChatEntry } from "@/types/chat";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { voiceAgentIdsMap, voiceAgentRequiresAuth } from "@/config/chat.config";
import { fetchVoiceSessionAuth } from "@/services/voiceSessionService";
import { ContentCard } from "@/components/ui/ContentCard";
import { useSession } from "@/hooks/useSession";
import { AIMentorPageActionsFooter, ChatHistory } from "./components";
import { ChatMessage } from "./TextAgentPage";
import { useAnalytics } from "@/lib/posthog";
import { readChatTextAttachment } from "@/lib/chatAttachmentText";

const AUTO_START_CONVERSATION = true;

export const VoiceAgentPage = ({ toggleInputMode, chatEntry }: { toggleInputMode?: () => void; chatEntry: ChatEntry }) => {
  const { userData } = useAuth();
  const { toast } = useToast();
  const { sessions, getSession } = useSession();
  const { trackVoiceAgentStart, trackVoiceAgentStop } = useAnalytics();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const sessionIdVoice = sessions?.[chatEntry]?.id;

  useEffect(() => {
    void getSession(chatEntry, true);
  }, [chatEntry, getSession]);

  const isInitialized = useRef<boolean>(false);
  const sessionStartTime = useRef<number | null>(null);
  const conversationId = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const conversation = useConversation({
    onError: (error) => console.error("Error:", error),
    onMessage: (props: { source: "user" | "ai"; message: string }) => {
      const role = props.source === "user" ? "user" : "assistant";
      setMessages((prev) => [...prev, createChatMessage(role, props.message)]);
    },
  });

  const startConversation = useCallback(async () => {
    if (conversation.status === "connected" || !voiceAgentIdsMap[chatEntry]) return;

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      if (voiceAgentRequiresAuth.has(chatEntry)) {
        const auth = await fetchVoiceSessionAuth(chatEntry, "webrtc");
        if (auth.connectionType === "webrtc") {
          await conversation.startSession({
            conversationToken: auth.conversationToken,
            connectionType: "webrtc",
          });
        } else {
          await conversation.startSession({
            signedUrl: auth.signedUrl,
            connectionType: "websocket",
          });
        }
      } else {
        await conversation.startSession({
          agentId: voiceAgentIdsMap[chatEntry],
          connectionType: "websocket",
        });
      }

      // Track voice agent start
      sessionStartTime.current = Date.now();
      conversationId.current = `${chatEntry}_${Date.now()}`;
      trackVoiceAgentStart({
        agentId: voiceAgentIdsMap[chatEntry],
        agentType: chatEntry,
        conversationId: conversationId.current,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Failed to start conversation",
        description: (
          <span>
            {message}. If problem persists, please
            <u className="ml-1">
              <a href="mailto:support@bodhi.ai">contact support.</a>
            </u>
          </span>
        ),
        variant: "destructive",
      });
      console.error(
        `Failed to start conversation with ${chatEntry} voice agent (id: ${voiceAgentIdsMap[chatEntry]})`,
        error
      );
    }
  }, [conversation, chatEntry, toast, trackVoiceAgentStart]);

  const stopConversation = useCallback(async () => {
    // Track voice agent stop before ending session
    if (sessionStartTime.current) {
      const duration = Math.floor((Date.now() - sessionStartTime.current) / 1000);
      trackVoiceAgentStop({
        agentId: voiceAgentIdsMap[chatEntry],
        agentType: chatEntry,
        conversationId: conversationId.current || undefined,
        duration,
      });
      sessionStartTime.current = null;
      conversationId.current = null;
    }

    await conversation.endSession();
  }, [conversation, chatEntry, trackVoiceAgentStop]);

  useEffect(() => {
    if (AUTO_START_CONVERSATION && !isInitialized.current) {
      isInitialized.current = true;
      startConversation();
    }
  }, [startConversation]);

  const handleFilesSelected = useCallback(
    async (files: FileList) => {
      const file = files[0];
      if (!file) return;
      const result = await readChatTextAttachment(file);
      if (!result.ok) {
        if (result.code === "size") {
          toast({
            title: "File too large",
            description: "Please use a text file under 256 KB for now.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Unsupported file type",
            description: "Try a .txt or .md file. PDFs are not inlined in voice mode yet.",
          });
        }
        return;
      }
      setMessages((prev) => [...prev, createChatMessage("user", result.body)]);
    },
    [toast]
  );

  const handleControlButtonClick = () => {
    if (conversation.status === "disconnected") {
      startConversation();
    } else if (conversation.status === "connected") {
      stopConversation();
      setMessages([]);
    }
  };

  const color = conversation.isSpeaking ? "#d08700" : "#4a9d9c";
  const frequency = conversation.isSpeaking ? 20 : 2;
  const amplitude = conversation.isSpeaking ? 1.6 : 0.6;
  const speed = conversation.isSpeaking ? 0.15 : 0.008;

  const isConnected = conversation.status === "connected";
  const isDisconnected = conversation.status === "disconnected";
  const isWaiting = conversation.status === "connecting" || conversation.status === "disconnecting";

  return (
    <>
      <ContentCard className="relative flex min-h-[500px] flex-1 flex-col border-2 border-[#C58C10] px-0 md:px-0">
        <ChatHistory
          className="flex-1 overflow-y-auto mb-2"
          revisionKey={chatEntry}
          messages={messages}
          isLoading={conversation.status === "connected" && !messages.length}
          userName={userData?.user_name || ""}
          isInitialization={conversation.status === "connecting"}
          isVoiceMode={true}
        />

        <div className="min-h-16 border-t pt-4 flex items-end gap-2 space-x-0 px-4 md:px-6 md:gap-3">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            aria-hidden
            accept=".txt,.md,.csv,text/plain"
            onChange={(e) => {
              const list = e.target.files;
              if (list?.length) void handleFilesSelected(list);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            aria-label="Attach a text file for context"
            title="Attach .txt or .md"
            disabled={isWaiting}
            onClick={() => fileInputRef.current?.click()}
            className="w-10 h-10 shrink-0 self-center rounded-full border border-gray-200 text-bodhi-teal hover:bg-stone-50 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5" />
          </button>
          <div className="flex-1 flex items-center justify-center min-h-[40px] px-2 md:px-12 md:pt-2">
            {isConnected && (
              <Siriwave
                color={color}
                cover
                speed={speed}
                amplitude={amplitude}
                frequency={frequency}
                width={240}
                height={30}
              />
            )}
          </div>

          <button
            type="button"
            disabled={isWaiting}
            onClick={handleControlButtonClick}
            className={cn(
              "w-10 h-10 bg-bodhi-teal self-center rounded-full flex items-center justify-center text-white hover:bg-bodhi-teal/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0",
              { "animate-pulse": isDisconnected || isWaiting }
            )}
          >
            {isConnected ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>

          {toggleInputMode && (
            <button
              type="button"
              title="Switch to text chat"
              aria-label="Switch to text chat"
              onClick={() => {
                stopConversation();
                toggleInputMode();
              }}
              className="ml-auto w-11 h-11 shrink-0 self-center rounded-full flex items-center justify-center text-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageSquareText className="w-5 h-5 text-bodhi-teal" />
            </button>
          )}
        </div>
      </ContentCard>

      <div className="mt-2 flex w-full shrink-0 flex-col gap-2.5">
        <AIMentorPageActionsFooter
          sessionId={sessionIdVoice}
          hasTranscript={messages.some((m) => m.role === "user")}
          transcriptLines={
            messages.length
              ? messages.map((m) => ({
                role: m.role,
                content: m.content,
              }))
              : undefined
          }
        />
      </div>
    </>
  );
};

const createChatMessage = (role: "user" | "assistant", content: string): ChatMessage => {
  return {
    id: Math.random().toString(36).substring(2, 9),
    role,
    content,
    timestamp: new Date(),
  };
};
